import axios from "axios";
import MapPlugin from "../MapPlugin";
import Route from "../../../../../common/Route";
import { GeoJsonPoint } from "../../../../../common/maphelpers";
import { useTranslation } from "react-i18next";
import { useOperationsMap } from "../../OperationsMapProvider";
import LayerSettings from "../../common/LayerSettings";
import { FormControl, InputLabel, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useEntities } from "@emberly/zenith-client";
import { MissionEnumsLists } from "../../../../../common/constants";



export default class HeatmapLayer extends MapPlugin {

  constructor(map) {
    super("heatmap", map);

    this.defaultSettings = {
      seasons: [getSeason()],
      days: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
      time: ["morning", "day", "afternoon", "night"],
      cause: "",
      missionType: ""
    };
  }


  async onMount() {
    try {

      const res = await axios.post(
        "/api/mission/statistics/locations",
        this.convertSettingsToQuery(this.settings),
        {
          headers: { Authorization: `Bearer ${await this.getAccessToken()}` },
        }
      );

      this._map.addSource("heatmap-locations", {
        type: "geojson",
        data: Route.GeoJson(
          res.data?.filter(t => t.c?.length === 2)?.map(loc => GeoJsonPoint(loc.c, { dbh: 16 }))
        )
      });

      this._map.addLayer({
        type: "heatmap",
        source: "heatmap-locations",
        slot: "elevation-2",
        id: "heatmap-layer",
        paint: {
          // increase weight as diameter breast height increases
          "heatmap-weight": {
            property: "dbh",
            type: "exponential",
            stops: [
              [1, 0],
              [62, 1]
            ]
          },
          // increase intensity as zoom level increases
          "heatmap-intensity": {
            stops: [
              [11, 1],
              [20, 3]
            ]
          },
          // assign color values be applied to points depending on their density
          "heatmap-color": [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0,
            "rgba(33,102,172,0)",
            0.2,
            "rgb(103,169,207)",
            0.4,
            "rgb(209,229,240)",
            0.6,
            "rgb(253,219,199)",
            0.8,
            "rgb(239,138,98)",
            1,
            "rgb(178,24,43)"
          ],
          // increase radius as zoom increases
          "heatmap-radius": {
            stops: [
              [7, 10],
              [11, 16],
              [15, 22],
              [20, 28]
            ]
          },
          // decrease opacity to transition into the circle layer
          "heatmap-opacity": {
            default: 1,
            stops: [
              [14, 1],
              [20, 0]
            ]
          }
        }
      });


    } catch (err) {
      console.log(err);
    }
  }

  onUnMount() {
    this._map.removeLayer("heatmap-layer");
    this._map.removeSource("heatmap-locations");
  }


  async onUpdateSettings(settings) {
    try {

      const res = await axios.post(
        "/api/mission/statistics/locations",
        this.convertSettingsToQuery(settings),
        {
          headers: { Authorization: `Bearer ${await this.getAccessToken()}` },
        }
      );

      this._map.getSource("heatmap-locations")?.setData(
        Route.GeoJson(
          res.data?.filter(t => t.c?.length === 2)?.map(loc => GeoJsonPoint(loc.c, { dbh: 16 }))
        )
      );

    } catch (err) {
      console.log(err);
    }
  }

  convertSettingsToQuery(settings) {
    return {
      months: !!settings.seasons ? convertSeasonsToMonthsQuery(settings.seasons) : undefined,
      daysOfWeek: !!settings.days ? convertDaysToDaysQuery(settings.days) : undefined,
      timeOfDay: !!settings.time ? convertTimeToTimeQuery(settings.time) : undefined,
      causeId: !!settings.cause ? settings.cause : undefined,
      missionType: typeof settings.missionType === "number" ? settings.missionType : undefined
    };
  }

}


export function HeatmapLayerSettings() {
  const { t } = useTranslation();
  const { operationsMap } = useOperationsMap();
  const settings = operationsMap.getSettings("heatmap");

  const { entities: causes } = useEntities("Cause");
  const causesSorted = useMemo(() => [...causes].sort((a, b) => a.title?.localeCompare(b.title)), [causes]);

  const [active, setActive] = useState(operationsMap.isPluginActive("heatmap"));
  const [seasons, setSeasons] = useState(settings.seasons); // TODO update data from operationsmap
  const [days, setDays] = useState(settings.days);
  const [time, setTime] = useState(settings.time);
  const [cause, setCause] = useState(settings.cause);
  const [missionType, setMissionType] = useState(settings.missionType);

  useEffect(() => {
    const fn = () => {
      setSeasons(settings.seasons);
      setDays(settings.days);
      setTime(settings.time);
      setCause(settings.cause);
      setMissionType(settings.missionType);
      setActive(operationsMap.isPluginActive("heatmap"));
    };

    operationsMap.on("reset", fn);

    return () => operationsMap.off("reset", fn);
  }, [operationsMap, settings]);

  // Handle plugin active state
  const onHandlePlugin = useCallback((ev, val) => {
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("heatmap");
    } else {
      operationsMap.deactivatePlugin("heatmap");
    }
  }, [operationsMap]);

  // Handle season
  const onHandleSeasons = useCallback((ev, next) => {
    setSeasons(next);
    operationsMap.updateSettings("heatmap", { seasons: next })
  }, [operationsMap]);

  // Handle days of week
  const onHandleDays = useCallback((ev, next) => {
    setDays(next);
    operationsMap.updateSettings("heatmap", { days: next })
  }, [operationsMap]);

  // Handle time of day
  const onHandleTime = useCallback((ev, next) => {
    setTime(next);
    operationsMap.updateSettings("heatmap", { time: next })
  }, [operationsMap]);

  // Handle time of day
  const onHandleCause = useCallback((ev) => {
    setCause(ev.target.value);
    operationsMap.updateSettings("heatmap", { cause: ev.target.value })
  }, [operationsMap]);

  const onHandleMissionType = useCallback((ev) => {
    setMissionType(ev.target.value);
    operationsMap.updateSettings("heatmap", { missionType: ev.target.value });
  }, [operationsMap]);

  return (
    <LayerSettings title={t("operationsMap:layers:heatmap:title")} open={active} onChange={onHandlePlugin}>
      <Stack spacing={1}>

        <ToggleButtonGroup
          color="primary"
          value={seasons}
          onChange={onHandleSeasons}
          fullWidth
        >
          <ToggleButton value="winter">{t("operationsMap:layers:heatmap:winter")}</ToggleButton>
          <ToggleButton value="spring">{t("operationsMap:layers:heatmap:spring")}</ToggleButton>
          <ToggleButton value="summer">{t("operationsMap:layers:heatmap:summer")}</ToggleButton>
          <ToggleButton value="autumn">{t("operationsMap:layers:heatmap:autumn")}</ToggleButton>
        </ToggleButtonGroup>


        <ToggleButtonGroup
          color="primary"
          value={days}
          onChange={onHandleDays}
          fullWidth
        >
          <ToggleButton value="monday">{t("operationsMap:layers:heatmap:monday")}</ToggleButton>
          <ToggleButton value="tuesday">{t("operationsMap:layers:heatmap:tuesday")}</ToggleButton>
          <ToggleButton value="wednesday">{t("operationsMap:layers:heatmap:wednesday")}</ToggleButton>
          <ToggleButton value="thursday">{t("operationsMap:layers:heatmap:thursday")}</ToggleButton>
          <ToggleButton value="friday">{t("operationsMap:layers:heatmap:friday")}</ToggleButton>
          <ToggleButton value="saturday">{t("operationsMap:layers:heatmap:saturday")}</ToggleButton>
          <ToggleButton value="sunday">{t("operationsMap:layers:heatmap:sunday")}</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          color="primary"
          value={time}
          onChange={onHandleTime}
          fullWidth
        >
          <ToggleButton value="morning">{t("operationsMap:layers:heatmap:morning")}</ToggleButton>
          <ToggleButton value="day">{t("operationsMap:layers:heatmap:day")}</ToggleButton>
          <ToggleButton value="afternoon">{t("operationsMap:layers:heatmap:afternoon")}</ToggleButton>
          <ToggleButton value="night">{t("operationsMap:layers:heatmap:night")}</ToggleButton>
        </ToggleButtonGroup>

        <FormControl fullWidth size="small" variant="filled">
          <InputLabel>{t("mission:cards:details:type")}</InputLabel>
          <Select
            onChange={onHandleMissionType}
            value={missionType || ""}
          >
            <MenuItem value="">{t("common:all")}</MenuItem>
            {MissionEnumsLists.Type.map((type, value) => <MenuItem value={value} key={value}>{t(`mission:enums:type:${type}`)}</MenuItem>)}
          </Select>
        </FormControl>


        <FormControl fullWidth size="small" variant="filled">
          <InputLabel>{t("mission:cards:details:cause")}</InputLabel>
          <Select
            onChange={onHandleCause}
            value={cause || ""}
          >
            <MenuItem value="">{t("common:all")}</MenuItem>
            {causesSorted.map((c, key) => <MenuItem value={c.id} key={key}>{c.title}</MenuItem>)}
          </Select>
        </FormControl>

      </Stack>
    </LayerSettings>
  );
}



const convertSeasonsToMonthsQuery = (seasons) => {
  if (seasons.length === 4) return undefined;

  let result = [];

  for (let i = 0; i < seasons.length; i++) {
    switch (seasons[i]) {

      case "winter":
        result.push([11, 0, 1]);
        break;
      case "spring":
        result.push([2, 3, 4]);
        break;
      case "summer":
        result.push([5, 6, 7]);
        break;
      case "autumn":
        result.push([8, 9, 10]);

      default:
        break;
    }
  }

  return result.flatMap(t => t);
};

const convertDaysToDaysQuery = (days) => {
  if (days.length === 7) return undefined;

  let result = [];

  for (let i = 0; i < days.length; i++) {
    switch (days[i]) {
      case "monday":
        result.push(2);
        break;
      case "tuesday":
        result.push(3);
        break;
      case "wednesday":
        result.push(4);
        break;
      case "thursday":
        result.push(5);
        break;
      case "friday":
        result.push(6);
        break;
      case "saturday":
        result.push(7);
        break;
      case "sunday":
        result.push(1);
        break;
      default:
        break;
    }
  }

  return result;
}


const convertTimeToTimeQuery = (days) => {
  if (days.length === 4) return undefined;

  let result = [];

  for (let i = 0; i < days.length; i++) {
    switch (days[i]) {
      case "morning":
        result.push({ startHour: 0, endHour: 6 });
        break;
      case "day":
        result.push({ startHour: 6, endHour: 12 });
        break;
      case "afternoon":
        result.push({ startHour: 12, endHour: 18 });
        break;
      case "night":
        result.push({ startHour: 18, endHour: 24 });
        break;
      default:
        break;
    }
  }

  return result;
}

const getSeason = () => {
  switch (new Date().getMonth()) {
    case 11:
    case 0:
    case 1:
      return "winter";

    case 2:
    case 3:
    case 4:
      return "spring";

    case 5:
    case 6:
    case 7:
      return "summer";

    default:
      return "autumn";
  }
};